import { Ng1StateDeclaration, StateParams } from "@uirouter/angularjs";

import { vsComponentName as processComponentName } from "../views/ProcessGroupGrid/component";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../records/grids/settings/gridSettingsService";
import {
  ProcessInformationService,
  vsServiceName as ProcessInformationServiceName,
} from "../services/processInformationService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import { ProcessGroup, ProcessInformation } from "../model/ProcessGroups";
import { ServiceResponse } from "../../records/server/query";
import { ProcessGroupConfig } from "./ProcessGroupConfig";
import {
  AuthorizedUiElementConfig,
  AuthorizedUiElementServiceProvider,
} from "../../ui/authorizedUiElement";
import { IPromise, Injectable } from "angular";
import { ColDef } from "../../records/grids/columns/colDef";
import { ProcessApiResponse } from "../model/ProcessApi";
import { noteAddState, noteState } from "../views/processDetail/notes/route";
import { addProcessDetailRoutes } from "../views/processDetail/routes";
import { timelineState } from "../views/processDetail/timeline/route";

export function configureDetailView<
  TDetailTabProvider extends
    AuthorizedUiElementServiceProvider<AuthorizedUiElementConfig>,
>(
  config: ProcessGroupConfig,
  detailTabsProvider: TDetailTabProvider,
  defaultQueryFactory: Injectable<(...args: any[]) => any>,
  onNewProcessSelected: Injectable<
    (
      ...args: any[]
    ) => Injectable<(process: ProcessInformation) => IPromise<any>>
  >,
) {
  detailTabsProvider.addElement(
    getAuthorizedUiElementConfigForProcessGroup(
      config,
      detailTabsProvider.basePath,
      defaultQueryFactory,
      onNewProcessSelected,
    ),
  );
}

export function getAuthorizedUiElementConfigForProcessGroup(
  config: ProcessGroupConfig,
  basePath: string,
  defaultQueryFactory: Injectable<(...args: any[]) => any>,
  onNewProcessSelected: Injectable<
    (
      ...args: any[]
    ) => Injectable<(process: ProcessInformation) => IPromise<any>>
  >,
): AuthorizedUiElementConfig {
  return {
    id: "Process",
    displayName: "Process",
    uiSrefFragment: "process",
    stateDefinition: getProcessState(
      config.processGroupId,
      Symbol(`${basePath}.process.view`),
      defaultQueryFactory,
      onNewProcessSelected,
      ProcessInformationService.defaultAdditionalGridColumns,
    ),
    children: [
      {
        id: "detail",
        displayName: "Detail",
        uiSrefFragment: "detail",
        stateDefinition: getProcessDetailState(
          resolveByProcessGroupId(config.processGroupId),
        ),
        children: [
          {
            displayName: "Notes",
            uiSrefFragment: "notes",
            stateDefinition: noteState(),
            id: "notes",
            children: [
              {
                displayName: "Add Note",
                uiSrefFragment: "add",
                stateDefinition: noteAddState(),
                id: "notesAdd",
              },
            ],
          },
          {
            displayName: "Process Timeline",
            uiSrefFragment: "timeline",
            stateDefinition: timelineState(),
            id: "timeline",
            children: [],
          },
        ],
      },
    ],
  };
}

function resolveByProcessGroupId(processGroupId: string) {
  return [
    ProcessInformationServiceName,
    (processInformationService: ProcessInformationService) => {
      return processInformationService.getDetailTemplate(processGroupId);
    },
  ];
}

export function getProcessDetailState(
  templateProviderResolver: Injectable<(...args: any[]) => string>,
): Ng1StateDeclaration {
  return {
    url: "/detail/{processInstancesId:int}",
    templateProvider: templateProviderResolver,
    controller: [
      "$scope",
      "model",
      "process",
      "processInformation",
      "processGroup",
      (
        $scope: any,
        model: any,
        process: ProcessApiResponse<any>,
        processInformation: any,
        processGroup: any,
      ) => {
        $scope.model = model;
        $scope.process = process;
        $scope.processInformation = processInformation;
        $scope.processGroup = processGroup;
      },
    ],
    resolve: {
      model: [
        ServerUtilityName,
        "$stateParams",
        "processGroup",
        function (
          serverUtility: ServerUtility,
          $stateParams: StateParams,
          processGroup: ProcessGroup,
        ) {
          const processInstancesId = $stateParams["processInstancesId"];
          return serverUtility
            .sendApiRequest({
              method: "get",
              url: `/api${processGroup.Url}?processInstancesId=${processInstancesId}`,
            })
            .then((r) => (<ServiceResponse<unknown[]>>r).Results?.[0]);
        },
      ],
      process: [
        ServerUtilityName,
        "$stateParams",
        "processGroup",
        function (
          serverUtility: ServerUtility,
          $stateParams: StateParams,
          processGroup: ProcessGroup,
        ) {
          const processInstancesId = $stateParams["processInstancesId"];
          return serverUtility
            .sendApiRequest({
              method: "get",
              url: `/api${processGroup.Url}/${processInstancesId}`,
            })
            .then((r) => (<ServiceResponse<unknown>>r).Result);
        },
      ],
      processInformation: [
        "processGroup",
        "model",
        (processGroup: ProcessGroup, model: { Type: string }) =>
          processGroup.ProcessInformation.find((p) => p.Type === model.Type),
      ],
    },
  };
}

export const nullSymbol = Symbol("onlyUsedForEqualityCheckToFallBackToDefault");
export function getProcessState(
  groupId: string,
  gridSettingsName: symbol,
  defaultQueryFactory: Injectable<(...args: any[]) => any>,
  onNewProcessSelected: Injectable<
    (
      ...args: any[]
    ) => Injectable<(process: ProcessInformation) => IPromise<any>>
  >,
  additionalGridColumns: Injectable<(...args: any[]) => ColDef[]>,
): Ng1StateDeclaration {
  return {
    url: "/process",
    component: processComponentName,
    resolve: {
      processGroup: [
        ProcessInformationServiceName,
        (processInformationService: ProcessInformationService) => {
          return processInformationService.getProcessGroup(groupId);
        },
      ],
      gridSettings: [
        GridSettingsServiceName,
        ProcessInformationServiceName,
        "processGroup",
        (
          gridSettingsService: GridSettingsService,
          processInformationService: ProcessInformationService,
          processGroup: ProcessGroup,
        ) => {
          return gridSettingsName === nullSymbol
            ? processInformationService.getGridSettings(processGroup)
            : gridSettingsService.getGridSettings(gridSettingsName);
        },
      ],
      onNewProcessSelected,
      additionalGridColumns,
      defaultQuery: defaultQueryFactory,
    },
  };
}
